// ForgotPassword.js
import React, { useState, useEffect } from "react";
import { Eye, EyeOff, Mail, ArrowRight, CheckCircle } from "lucide-react";
import classes from "./forgotPassword.module.css";
import Footer from "../../components/footer";
import { useDispatch } from "react-redux";
import { forgotPassword, resetPassword } from "../../redux/slices/loginSlice";
// import { requestPasswordReset, resetPassword } from "./api-service";

// Step 1: Request Password Reset
export const RequestPasswordReset = () => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [error, setError] = useState("");
  const dispatch = useDispatch();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      const resp = await dispatch(forgotPassword({ email }));
      if (resp) {
        setEmailSent(true);
        setIsLoading(false);
      }
      //   await requestPasswordReset(email);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className={classes.container}>
        <div className={classes.card}>
          <h1 className={classes.title}>Forgot Password</h1>

          {!emailSent ? (
            <>
              <p className={classes.subtitle}>
                Enter your email address and we'll send you a link to reset your
                password.
              </p>

              <form onSubmit={handleSubmit} className={classes.form}>
                <div className={classes.inputGroup}>
                  <label htmlFor="email" className={classes.label}>
                    Email
                  </label>
                  <div className={classes.inputWrapper}>
                    <Mail className={classes.inputIconEmail} size={18} />
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your@email.com"
                      required
                      className={classes.input}
                    />
                  </div>
                </div>

                {error && <p className={classes.error}>{error}</p>}

                <button
                  type="submit"
                  className={classes.button}
                  disabled={isLoading}
                >
                  {isLoading ? "Sending..." : "Send Reset Link"}
                  {!isLoading && (
                    <ArrowRight size={18} className={classes.buttonIcon} />
                  )}
                </button>
              </form>
            </>
          ) : (
            <div className={classes.successMessage}>
              <CheckCircle size={48} className={classes.successIcon} />
              <h2>Check your inbox</h2>
              <p>
                We've sent a password reset link to <strong>{email}</strong>.
                Please check your email and click on the link to reset your
                password.
              </p>
              <p className={classes.note}>
                If you don't see the email, check your spam folder or
                <button
                  onClick={() => setEmailSent(false)}
                  className={classes.textButton}
                >
                  try again
                </button>
              </p>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

// Step 2: Reset Password
export const ResetPassword = ({ token }) => {
  // Using token directly from props instead of Next.js router

  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [resetSuccess, setResetSuccess] = useState(false);

  const dispatch = useDispatch();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      dispatch(resetPassword({ token, password: formData.password }));
      // Use the API service function
      //   await resetPassword(token, formData.password);
      setResetSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // If no token is present in URL
  if (!token) {
    return (
      <div className={classes.container}>
        <div className={classes.card}>
          <h1 className={classes.title}>Invalid Link</h1>
          <p className={classes.subtitle}>
            This password reset link is invalid or has expired. Please request a
            new password reset link.
          </p>
          <button
            onClick={() => (window.location.href = "/forgot-password")}
            className={classes.button}
          >
            Back to Forgot Password
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={classes.container}>
        <div className={classes.card}>
          {!resetSuccess ? (
            <>
              <h1 className={classes.title}>Reset Your Password</h1>
              <p className={classes.subtitle}>
                Create a new password for your Artist Next Door account.
              </p>

              <form onSubmit={handleSubmit} className={classes.form}>
                <div className={classes.inputGroup}>
                  <label htmlFor="password" className={classes.label}>
                    New Password
                  </label>
                  <div className={classes.inputWrapper}>
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Enter new password"
                      required
                      className={classes.input}
                    />
                    <button
                      type="button"
                      className={classes.visibilityToggle}
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff size={18} className={classes.inputIcon} />
                      ) : (
                        <Eye size={18} className={classes.inputIcon} />
                      )}
                    </button>
                  </div>
                </div>

                <div className={classes.inputGroup}>
                  <label htmlFor="confirmPassword" className={classes.label}>
                    Confirm Password
                  </label>
                  <div className={classes.inputWrapper}>
                    <input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      placeholder="Confirm new password"
                      required
                      className={classes.input}
                    />
                    <button
                      type="button"
                      className={classes.visibilityToggle}
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                    >
                      {showConfirmPassword ? (
                        <EyeOff size={18} className={classes.inputIcon} />
                      ) : (
                        <Eye size={18} className={classes.inputIcon} />
                      )}
                    </button>
                  </div>
                </div>

                {error && <p className={classes.error}>{error}</p>}

                <button
                  type="submit"
                  className={classes.button}
                  disabled={isLoading}
                >
                  {isLoading ? "Resetting..." : "Reset Password"}
                  {!isLoading && (
                    <ArrowRight size={18} className={classes.buttonIcon} />
                  )}
                </button>
              </form>
            </>
          ) : (
            <div className={classes.successMessage}>
              <CheckCircle size={48} className={classes.successIcon} />
              <h2>Password Reset Successfully</h2>
              <p>
                Your password has been reset successfully. You can now use your
                new password to log in.
              </p>
              <button
                onClick={() => (window.location.href = "/login")}
                className={classes.button}
              >
                Go to Login
                <ArrowRight size={18} className={classes.buttonIcon} />
              </button>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

// Main component that handles the routing between different password reset steps
export const ForgotPasswordFlow = () => {
  const [token, setToken] = useState("");

  useEffect(() => {
    // Get token from URL parameters
    const queryParams = new URLSearchParams(window.location.search);
    const tokenParam = queryParams.get("token");
    setToken(tokenParam || "");
  }, []);

  // If token is present in URL, show reset password form
  if (token) {
    return <ResetPassword token={token} />;
  }

  // Otherwise show request password reset form
  return <RequestPasswordReset />;
};
